/** AuthContext — текущий пользователь + login/logout. */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "../api/client";
import { resetQueryCache } from "../api/queryClient";

export type Role =
  | "purchaser"
  | "approver"
  | "operator"
  | "analyst"
  | "admin"
  /** Кладовщик: приёмка на планшете и ничего больше. */
  | "warehouse"
  | "supplier";

export interface AuthUser {
  id: string;
  email: string;
  full_name: string;
  role: Role;
  is_active: boolean;
  department_id: string | null;
  department_code: string | null;
  department_name: string | null;
  created_at: string | null;
  /** Доступ к «Отчётам» — действующий: у администратора всегда true. */
  reports_access?: boolean;
  /** Группы согласования — для кнопок решения по шагу «Группа». */
  approval_group_ids?: string[];
  /** Письма о событиях: each — на каждое, digest — сводка раз в день, none — только колокольчик. */
  notify_email?: "each" | "digest" | "none";
}

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  /** Принять готовую сессию (пароль задан по ссылке приглашения — T5). */
  adoptSession: (accessToken: string, user: AuthUser) => void;
  logout: () => void;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

const TOKEN_KEY = "rus_ii_token";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  });
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState<boolean>(!!token);

  // Authenticated fetch wrapper
  useEffect(() => {
    if (token) {
      // подмешиваем токен в fetch через перехватчик
      const origGet = api.get;
      const origPost = api.post;
      const origPatch = api.patch;
      const origDel = api.del;
      api.get = <T,>(path: string) =>
        fetch(`/api/v1${path}`, {
          credentials: "include",
          headers: { Authorization: `Bearer ${token}` },
        }).then(handleResponse<T>);
      api.post = <T,>(path: string, body?: unknown) =>
        fetch(`/api/v1${path}`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: body ? JSON.stringify(body) : undefined,
        }).then(handleResponse<T>);
      api.patch = <T,>(path: string, body?: unknown) =>
        fetch(`/api/v1${path}`, {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: body ? JSON.stringify(body) : undefined,
        }).then(handleResponse<T>);
      api.del = <T,>(path: string) =>
        fetch(`/api/v1${path}`, {
          method: "DELETE",
          credentials: "include",
          headers: { Authorization: `Bearer ${token}` },
        }).then(handleResponse<T>);
      return () => {
        api.get = origGet;
        api.post = origPost;
        api.patch = origPatch;
        api.del = origDel;
      };
    }
  }, [token]);

  const refresh = useCallback(async () => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const u = await api.get<AuthUser>("/auth/me");
      setUser(u);
    } catch {
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string): Promise<AuthUser> => {
      const resp = await fetch("/api/v1/auth/login", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!resp.ok) {
        const t = await resp.text().catch(() => "");
        throw new Error(t || resp.statusText);
      }
      const data = (await resp.json()) as { access_token: string; user: AuthUser };
      try {
        localStorage.setItem(TOKEN_KEY, data.access_token);
      } catch {
        // ignore
      }
      // Кеш принадлежит прежней сессии: вход в той же вкладке не
      // перезагружает страницу, и без очистки новый пользователь увидел
      // бы чужие данные.
      resetQueryCache();
      setToken(data.access_token);
      setUser(data.user);
      setLoading(false);
      return data.user;
    },
    [],
  );

  const adoptSession = useCallback((accessToken: string, nextUser: AuthUser) => {
    try {
      localStorage.setItem(TOKEN_KEY, accessToken);
    } catch {
      // ignore
    }
    resetQueryCache();
    setToken(accessToken);
    setUser(nextUser);
    setLoading(false);
  }, []);

  const logout = useCallback(() => {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      // ignore
    }
    // Выход обязан унести данные с собой: страница не перезагружается,
    // и всё загруженное прежним пользователем осталось бы в памяти
    // вкладки — и показалось бы следующему.
    resetQueryCache();
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, token, loading, login, adoptSession, logout, refresh }),
    [user, token, loading, login, adoptSession, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(AuthContext);
  if (!v) throw new Error("useAuth must be used inside <AuthProvider>");
  return v;
}

export function hasRole(user: AuthUser | null, ...roles: Role[]): boolean {
  if (!user) return false;
  if (user.role === "admin") return true;
  return roles.includes(user.role);
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}